import {
  ChangeDetectionStrategy, Component, EventEmitter, Input, Output
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import isEmpty from 'lodash-es/isEmpty';

import { isNullOrUndefined } from '@app/util/util';
import { AssetCards } from '@asset-cards';
import { Card } from '@app/entities/cards';
import { environment as ENV } from '@environment';
import { CurrencyFormatPipe } from '@app/pipes/currency-format.pipe';
import { getCardsSubtitle } from '@shared/benefits/mappers/subtitles.mapper';
import { cardsPartnerCollection, CARD_EXCEPTIONS } from '@app/modules/shared/benefits/mappers/partners-collections.mapper';

@Component({
  selector: 'app-card-partners',
  templateUrl: './card-partners.component.html',
  styleUrls: [
    '../partners-styles/partners-styles-base.sass'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CardPartnersComponent {

  @Input() totalPrice: number;
  @Input() maxAmount: number;
  @Input() isDesktop: boolean;
  @Input() showSwipeUp: boolean;

  @Output() closeModal: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private currencyFormat: CurrencyFormatPipe,
    private transloco: TranslocoService
  ) { }

  get cards(): Card[] {
    return cardsPartnerCollection(this.totalPrice);
  }

  get maxItems(): number {
    return this.enabledCardInstallments.length;
  }

  get enabledCardInstallments(): number[] {
    return AssetCards?.enabled_cards_installments || [];
  }

  get subtitle(): string {
    if (!this.validCardExceptionsBySubtitle) {
      return null;
    } else {
      return getCardsSubtitle(
        this.currencyFormat.transform(
          CARD_EXCEPTIONS.default_min_price_cards,
          ENV.countryInfo.currency_code
        ),
        this.currencyFormat.transform(
          CARD_EXCEPTIONS.card_items_min_price,
          ENV.countryInfo.currency_code
        ),
        CARD_EXCEPTIONS.card_subtitle,
        CARD_EXCEPTIONS.card_items,
        this.transloco.translate('INSTALLMENTS_INFO.SUBTITLE_LAST_CONCAT')
      );
    }
  }

  private get defaultCardIndex(): number {
    return this.enabledCardInstallments.findIndex(
      instalment => instalment === this.maxAmount);
  }

  private get validCardExceptionsBySubtitle(): boolean {
    return !isEmpty(CARD_EXCEPTIONS) &&
      !isNullOrUndefined(CARD_EXCEPTIONS?.card_items_min_price) &&
      !isNullOrUndefined(CARD_EXCEPTIONS?.default_min_price_cards) &&
      !!CARD_EXCEPTIONS?.card_items?.length &&
      !isEmpty(CARD_EXCEPTIONS?.card_subtitle);
  }

  public buildAlternativeTextKey(position: number): string {
    return AssetCards?.installments_text[position]?.value;
  }

  public isDefaultCardInstallment(position: number): boolean {
    return this.defaultCardIndex === position;
  }

  public installments(installments: number[]): number[] {
    const installmentsLength = installments.length;
    const addItems = this.maxItems - installmentsLength;
    return installments.concat(new Array(addItems).fill(null));
  }

  public installmentIcon(installment: number, position: number): string {
    return this.isQuotaEnabled(installment, position) ? 'icon-simple-check' : 'icon-close';
  }

  private isQuotaEnabled(installment: number, position: number): boolean {
    return !isNullOrUndefined(position)
      && !!this.enabledCardInstallments[position]
      && installment === this.enabledCardInstallments[position];
  }
}
